﻿import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createTariff,
  deleteTariff,
  listTariffItems,
  type TariffItem,
  type TariffMutationPayload,
  updateTariff,
} from "@/lib/api";

type TierForm = {
  fromM3: string;
  toM3: string;
  unitPrice: string;
};

type FormState = {
  code: string;
  name: string;
  fixedCharge: string;
  status: "active" | "inactive";
  validFrom: string;
  validTo: string;
  tiers: TierForm[];
};

const emptyForm: FormState = {
  code: "",
  name: "",
  fixedCharge: "",
  status: "active",
  validFrom: new Date().toISOString().slice(0, 10),
  validTo: "",
  tiers: [{ fromM3: "0", toM3: "", unitPrice: "0" }],
};

export default function TarifasTab() {
  const { session } = useSession();
  const [items, setItems] = useState<TariffItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listTariffItems(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar tarifas");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const title = useMemo(() => (editingId ? "Editar tarifa" : "Crear tarifa"), [editingId]);

  function setTier(index: number, field: keyof TierForm, value: string): void {
    setForm((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)),
    }));
  }

  function addTier(): void {
    setForm((prev) => ({ ...prev, tiers: [...prev.tiers, { fromM3: "", toM3: "", unitPrice: "" }] }));
  }

  function removeTier(index: number): void {
    setForm((prev) => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== index) }));
  }

  function toPayload(value: FormState): TariffMutationPayload {
    return {
      code: value.code.trim().toUpperCase(),
      name: value.name.trim(),
      fixedCharge: Number(value.fixedCharge || 0),
      status: value.status,
      validFrom: value.validFrom,
      validTo: value.validTo || undefined,
      tiers: value.tiers.map((tier, idx) => ({
        fromM3: Number(tier.fromM3 || 0),
        toM3: tier.toM3.trim() === "" ? null : Number(tier.toM3),
        unitPrice: Number(tier.unitPrice || 0),
        sortOrder: idx + 1,
      })),
    };
  }

  function loadEdit(item: TariffItem): void {
    setEditingId(item.id);
    setForm({
      code: item.code,
      name: item.name,
      fixedCharge: String(item.fixedCharge),
      status: item.status === "inactive" ? "inactive" : "active",
      validFrom: item.validFrom.slice(0, 10),
      validTo: item.validTo ? item.validTo.slice(0, 10) : "",
      tiers: item.tiers.length
        ? item.tiers.map((tier) => ({
            fromM3: String(tier.fromM3),
            toM3: tier.toM3 == null ? "" : String(tier.toM3),
            unitPrice: String(tier.unitPrice),
          }))
        : [{ fromM3: "0", toM3: "", unitPrice: "0" }],
    });
  }

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function openCreateModal(): void {
    resetForm();
    setShowCreateModal(true);
  }

  function closeCreateModal(): void {
    setShowCreateModal(false);
    resetForm();
  }

  async function onSave(): Promise<void> {
    if (!session) return;
    if (!form.code.trim() || !form.name.trim() || !form.validFrom) {
      setError("Completa código, nombre y vigencia desde");
      return;
    }
    if (form.tiers.length === 0) {
      setError("Debes ingresar al menos un tramo");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(form);
      if (editingId) {
        const updated = await updateTariff(session, editingId, payload);
        setItems((prev) => prev.map((item) => (item.id === editingId ? updated : item)));
      } else {
        const created = await createTariff(session, payload);
        setItems((prev) => [created, ...prev]);
        closeCreateModal();
      }
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar tarifa");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!session) return;
    if (!window.confirm("¿Eliminar tarifa? Esta acción no se puede deshacer.")) return;
    try {
      await deleteTariff(session, id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar tarifa");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
          {!editingId && (
            <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              <i className="ri-add-line" />
              Nueva tarifa
            </button>
          )}
        </div>
        {!editingId ? (
          <p className="text-sm text-gray-500">Usa “Nueva tarifa” para crear registros. La edición se hace desde la lista.</p>
        ) : null}
        {editingId ? (
        <>
        <div className="grid grid-cols-4 gap-3 mb-3">
          <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} placeholder="Código" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nombre" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="number" value={form.fixedCharge} onChange={(e) => setForm((p) => ({ ...p, fixedCharge: e.target.value }))} placeholder="Cargo fijo" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as "active" | "inactive" }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input type="date" value={form.validFrom} onChange={(e) => setForm((p) => ({ ...p, validFrom: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="date" value={form.validTo} onChange={(e) => setForm((p) => ({ ...p, validTo: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>

        <div className="space-y-2 mb-4">
          <div className="text-xs font-semibold text-gray-700">Tramos</div>
          {form.tiers.map((tier, index) => (
            <div key={`tier-${index}`} className="grid grid-cols-4 gap-2">
              <input type="number" value={tier.fromM3} onChange={(e) => setTier(index, "fromM3", e.target.value)} placeholder="Desde" className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg" />
              <input type="number" value={tier.toM3} onChange={(e) => setTier(index, "toM3", e.target.value)} placeholder="Hasta (vacío=sin tope)" className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg" />
              <input type="number" value={tier.unitPrice} onChange={(e) => setTier(index, "unitPrice", e.target.value)} placeholder="Precio" className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg" />
              <button type="button" onClick={() => removeTier(index)} disabled={form.tiers.length === 1} className="px-2 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg disabled:opacity-50">Quitar</button>
            </div>
          ))}
          <button type="button" onClick={addTier} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">Agregar tramo</button>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60">{saving ? "Guardando..." : editingId ? "Actualizar tarifa" : "Crear tarifa"}</button>
          {editingId && (
            <button onClick={resetForm} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancelar edición</button>
          )}
        </div>
        </>
        ) : null}
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-4">Tarifas ({items.length})</h2>
        {loading ? (
          <div className="h-16 bg-gray-100 rounded animate-pulse" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">Sin tarifas registradas.</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className="border border-gray-100 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-800">{item.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{item.code} • Cargo fijo: ${item.fixedCharge.toLocaleString("es-CL")}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${item.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{item.status}</span>
                      <button onClick={() => loadEdit(item)} className="text-xs px-2 py-1 border border-gray-200 rounded">Editar</button>
                      <button onClick={() => onDelete(item.id)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded">Eliminar</button>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {item.tiers.map((tier) => `${tier.fromM3}-${tier.toM3 ?? "∞"}: $${tier.unitPrice.toLocaleString("es-CL")}`).join(" | ")}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Nueva tarifa</h3>
              <button onClick={closeCreateModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"><i className="ri-close-line" /></button>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-3">
              <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} placeholder="Código" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nombre" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input type="number" value={form.fixedCharge} onChange={(e) => setForm((p) => ({ ...p, fixedCharge: e.target.value }))} placeholder="Cargo fijo" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as "active" | "inactive" }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="active">Activa</option>
                <option value="inactive">Inactiva</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <input type="date" value={form.validFrom} onChange={(e) => setForm((p) => ({ ...p, validFrom: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input type="date" value={form.validTo} onChange={(e) => setForm((p) => ({ ...p, validTo: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div className="space-y-2 mb-4">
              <div className="text-xs font-semibold text-gray-700">Tramos</div>
              {form.tiers.map((tier, index) => (
                <div key={`tier-modal-${index}`} className="grid grid-cols-4 gap-2">
                  <input type="number" value={tier.fromM3} onChange={(e) => setTier(index, "fromM3", e.target.value)} placeholder="Desde" className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg" />
                  <input type="number" value={tier.toM3} onChange={(e) => setTier(index, "toM3", e.target.value)} placeholder="Hasta (vacío=sin tope)" className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg" />
                  <input type="number" value={tier.unitPrice} onChange={(e) => setTier(index, "unitPrice", e.target.value)} placeholder="Precio" className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg" />
                  <button type="button" onClick={() => removeTier(index)} disabled={form.tiers.length === 1} className="px-2 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg disabled:opacity-50">Quitar</button>
                </div>
              ))}
              <button type="button" onClick={addTier} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">Agregar tramo</button>
            </div>
            <div className="flex gap-3">
              <button onClick={closeCreateModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancelar</button>
              <button onClick={() => void onSave()} disabled={saving} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60">{saving ? "Creando..." : "Crear tarifa"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
